import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { CheckCircle, WarningCircle, ChartBar } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { scannerApi } from './scannerApi';
import { formatDate } from '@/utils/datetime';

const fmtDate = (s) => (s ? formatDate(s) : '—');

/**
 * 棚卸レポート (Verify Report) — shows IT assets verified vs stale.
 * Owned by the scanner app; sits alongside the verify mode.
 */
export default function VerifyReportDialog({ open, onClose }) {
  const [days, setDays] = useState(90);
  const [report, setReport] = useState(null);
  const [tab, setTab] = useState('stale');

  useEffect(() => {
    if (!open) return;
    setReport(null);
    scannerApi.verifyReport({ days })
      .then(setReport)
      .catch(() => toast.error('レポートの取得に失敗しました'));
  }, [open, days]);

  const rows = report ? (tab === 'stale' ? report.stale : report.verified) : [];

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" aria-describedby={undefined} data-testid="verify-report-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ChartBar size={20} weight="duotone" className="text-teal-500" />
            IT資産 棚卸レポート
          </DialogTitle>
        </DialogHeader>

        <div className="flex items-center gap-3 flex-wrap py-2">
          <label className="text-xs text-zinc-500">対象期間</label>
          <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
            <SelectTrigger data-testid="verify-report-days" className="h-9 w-32"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="30">直近30日</SelectItem>
              <SelectItem value="90">直近90日</SelectItem>
              <SelectItem value="180">直近半年</SelectItem>
              <SelectItem value="365">直近1年</SelectItem>
            </SelectContent>
          </Select>

          {report && (
            <>
              <Badge variant="outline" className="bg-teal-50 text-teal-700 border-teal-200 ml-auto">
                <CheckCircle size={12} className="mr-1" /> 棚卸済: {report.verified_count}
              </Badge>
              <Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-200">
                <WarningCircle size={12} className="mr-1" /> 未棚卸: {report.stale_count}
              </Badge>
            </>
          )}
        </div>

        <div className="inline-flex items-center gap-1 rounded-lg bg-zinc-100 p-1">
          <button data-testid="verify-tab-stale"
            onClick={() => setTab('stale')}
            className={`px-3 py-1 text-xs font-semibold rounded ${tab === 'stale' ? 'bg-white text-rose-600 shadow-sm' : 'text-zinc-500 hover:text-zinc-800'}`}>
            未棚卸 ({report?.stale_count || 0})
          </button>
          <button data-testid="verify-tab-verified"
            onClick={() => setTab('verified')}
            className={`px-3 py-1 text-xs font-semibold rounded ${tab === 'verified' ? 'bg-white text-teal-600 shadow-sm' : 'text-zinc-500 hover:text-zinc-800'}`}>
            棚卸済 ({report?.verified_count || 0})
          </button>
        </div>

        <div className="rounded-lg border border-zinc-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-zinc-100 text-zinc-700 text-[11px]">
                <th className="text-left px-3 py-2">資産番号</th>
                <th className="text-left px-3 py-2">品名</th>
                <th className="text-left px-3 py-2">使用者</th>
                <th className="text-left px-3 py-2">場所</th>
                <th className="text-left px-3 py-2">最終棚卸</th>
              </tr>
            </thead>
            <tbody>
              {!report && <tr><td colSpan={5} className="px-3 py-8 text-center text-zinc-400 text-xs">読み込み中...</td></tr>}
              {report && rows.length === 0 && <tr><td colSpan={5} className="px-3 py-8 text-center text-zinc-400 text-xs">該当なし</td></tr>}
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-zinc-200" data-testid={`verify-row-${r.id}`}>
                  <td className="px-3 py-2 font-mono text-xs text-zinc-500">{r.asset_tag || '—'}</td>
                  <td className="px-3 py-2 text-zinc-800">{r.name}</td>
                  <td className="px-3 py-2 text-zinc-600">{r.assigned_to_name || <span className="text-zinc-300">未割当</span>}</td>
                  <td className="px-3 py-2 text-zinc-600">{r.location || <span className="text-zinc-300">—</span>}</td>
                  <td className="px-3 py-2 text-zinc-600">{fmtDate(r.last_verified_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </DialogContent>
    </Dialog>
  );
}
